import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBackendURL } from '../utils';
import ProgressTabs from '../components/ProgressTabs';
import SweatTextTitle from '../steps/SweatTextTitle';
import RemindersInfo from './RemindersInfo';
import { useAuth } from '../auth';
import { PhoneInput, defaultCountries,  parseCountry } from 'react-international-phone';
import 'react-international-phone/style.css';
import '../BasicReminder.css';

const StepTrainerPhone = () => {
  const { user, fetchUserInfo } = useAuth();
  const [phone, setPhone] = useState(user?.phone || '');
  const [syncingPhone, setSyncingPhone] = useState(false);
  const [syncedPhone, setSyncedPhone] = useState(false);
  const [syncError, setSyncError] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [showSpinner, setShowSpinner] = useState(false);
  const navigate = useNavigate();
  const debouncePhoneRef = useRef();
  const lastSyncedPhoneRef = useRef(user?.phone || '');


  // Call fetchUserInfo when component mounts to ensure we have the latest user data
  useEffect(() => {
    fetchUserInfo();
  }, []);

  useEffect(() => {
    // Simple international phone validation: starts with + and at least 10 digits
    const digits = phone.replace(/\D/g, '');
    setIsPhoneValid(phone.startsWith('+') && digits.length >= 10);
  }, [phone]);

  // if user changes, reload phone state
  useEffect(() => {
    setPhone(user?.phone || '');
    lastSyncedPhoneRef.current = user?.phone || '';
  }, [user]);

  // Debounced phone PUT
  useEffect(() => {
    if (phone === lastSyncedPhoneRef.current || !isPhoneValid) return;
    if (debouncePhoneRef.current) clearTimeout(debouncePhoneRef.current);
    debouncePhoneRef.current = setTimeout(async () => {
      setSyncingPhone(true);
      setSyncedPhone(false);
      setSyncError('');
      try {
        const userId = user?.user_id || user?.id;
        const res = await fetch(`${getBackendURL()}/user/${userId}?user_id=${userId}`, {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${window.localStorage.getItem('ptc_access_token') || ''}`
          },
          body: JSON.stringify({ phone }),
        });
        if (!res.ok) throw new Error('Failed to update phone');
        lastSyncedPhoneRef.current = phone;
        setSyncedPhone(true);
      } catch (err) {
        setSyncError(err.message);
      } finally {
        setSyncingPhone(false);
      }
    }, 2000);
    return () => clearTimeout(debouncePhoneRef.current);
  }, [phone, user, isPhoneValid]);

  useEffect(() => {
    if (!syncingPhone) setShowSpinner(false);
  }, [syncingPhone]);

  // If no user redirect to homepage.
  if (!user) {
    return (
      <div className="reminder-container fogged-card">
        Sorry, you must be logged in to access this page.
      </div>
    );
  } 
const countries = defaultCountries.filter((country) => {
  const { iso2 } = parseCountry(country);
  return ['us'].includes(iso2);
});

  return (
    <div className="reminder-container fogged-card">
      <SweatTextTitle />
      <RemindersInfo />
      <ProgressTabs currentStep={1} />
      <h2 className="step-title">Step 2: Enter Your Phone Number</h2>
      <p>As a trainer, you'll receive texts from our system. Please enter your US phone number (starting with "+1 ") to continue.</p>
      <label>
        <div style={{ margin: '1rem 0', display: 'flex', alignItems: 'center' }}>
          <div>
            <PhoneInput
              defaultCountry="us"
              countries={countries}
              value={phone}
              onChange={value => {
                setPhone(value);
                setShowSpinner(true);
                setSyncedPhone(false);
              }}
              disabled={syncingPhone}
              style={{
                background: !isPhoneValid ? '#ffb368ff' : undefined,
                border: !isPhoneValid ? 'solid 3px #ffb368ff' : undefined,
                borderRadius: '4px',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
          </div>
          {showSpinner && (
            <span className="spinner" style={{
              width: '1rem',
              height: '1rem',
              border: '2px solid #ccc',
              borderTop: '2px solid #000',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginLeft: '0.5rem',
            }}></span>
          )}
          {syncError && (
            <span style={{ color: 'red', marginLeft: '0.5rem', fontSize: '0.95rem' }}>{syncError}</span>
          )}
        </div>
      </label>
      <div style={{ margin: '1rem 0' }}>
        {syncedPhone && <span style={{ color: 'green', fontWeight: 'bold', marginLeft: '1rem' }}>✔ Changes saved!</span>}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
        <button className="ptc-secondary-button" onClick={() => navigate('/tools/reminders/step1')} style={{ padding: '1rem 2rem', fontSize: '1rem' }}>
          Previous Step
        </button>
        <button
          className="ptc-button"
          onClick={() => {
            // window.location.href='tools/reminders/step-consent';
            navigate('/tools/reminders/step-consent');
          }}
          style={{ padding: '1rem 2rem', 
            background: isPhoneValid && !syncingPhone ? '#6610f2' : '#ccc',
            color: isPhoneValid && !syncingPhone ? 'white' : '#222',
            border: 'none',
            cursor: isPhoneValid && !syncingPhone ? 'pointer' : 'not-allowed',
            fontSize: '1rem' }}
          disabled={!isPhoneValid || syncingPhone}
        >
          Next Step
        </button>
      </div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default StepTrainerPhone;
